import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { format } from 'date-fns';
import { it, enUS } from 'date-fns/locale';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useToast } from '../components/ui/use-toast';
import { Loader2, User, Mail, Settings, AlertCircle, Languages, Sun, Moon, Monitor, LogOut, Bot } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { profileService } from '../services/profile';
import { changeAppLanguage } from '../lib/language';
import { cn } from '../lib/utils';
import type { Theme } from '../lib/theme';
import { AvatarUpload } from '../components/avatar-upload';
import { PushNotificationToggle } from '../components/push-notification-toggle';
import { FeatureFlagToggle } from '../components/feature-flag-toggle';
import { CoachInviteLink } from '../components/coach-invite-link';
import MembershipCardsCard from '../components/MembershipCardsCard';
import MyAthletesCard from '../components/guardian/MyAthletesCard';
import { featureFlagsService } from '../services/feature-flags';
import { CONSENT_AGE_YEARS, isUnderConsentAge } from '../utils/consent-age';
import type { User as UserType, ProfileUpdateRequest } from '../types/user';

// Tipo per gli errori JSON:API
interface JsonApiError {
  status: string;
  source?: {
    pointer: string;
  };
  title: string;
  detail: string;
  code: string;
}

export default function ProfilePage() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === 'it' ? it : enUS;
  const navigate = useNavigate();
  const { token, updateUserData, signOut } = useAuth();
  const { theme, setTheme } = useTheme();
  const { toast } = useToast();

  useBreadcrumb(
    () => ({
      onBack: () => navigate(-1),
      segments: [{ label: t('profile.title') }],
    }),
    [t, navigate]
  );
  const currentLanguage: 'en' | 'it' = i18n.language?.startsWith('it') ? 'it' : 'en';
  const [user, setUser] = useState<UserType | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarSignedId, setAvatarSignedId] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    username: '',
    email: '',
    gender: '',
    birth_date: '',
    language: ''
  });

  // Said as soon as the date is whole, rather than left for the server to
  // refuse on save. Below 14 an account of one's own is not allowed (ConsentAge
  // on the server, which has the last word).
  const today = new Date().toISOString().slice(0, 10);
  const birthDateProblem =
    formData.birth_date.length !== 10
      ? null
      : formData.birth_date > today
        ? t('profile.birthDateInFuture')
        : isUnderConsentAge(formData.birth_date)
          ? t('profile.birthDateUnderAge', { years: CONSENT_AGE_YEARS })
          : null;

  const changeLanguage = (lang: 'en' | 'it') => {
    if (lang === currentLanguage) return;

    void changeAppLanguage(lang);
    // The form posts `language` as well: keep it from saving the previous one.
    setFormData(prev => ({ ...prev, language: lang }));
  };

  // Monitor online status
  useEffect(() => {
    function handleOnlineStatusChange() {
      // Online status monitoring removed for now
    }

    window.addEventListener('online', handleOnlineStatusChange);
    window.addEventListener('offline', handleOnlineStatusChange);

    return () => {
      window.removeEventListener('online', handleOnlineStatusChange);
      window.removeEventListener('offline', handleOnlineStatusChange);
    };
  }, []);

  // Load profile data
  useEffect(() => {
    if (!token) return;

    const loadProfile = async () => {
      try {
        setLoading(true);
        const response = await profileService.getProfile();
        setUser(response.user);
        setFormData({
          name: response.user.name || '',
          surname: response.user.surname || '',
          username: response.user.username || '',
          email: response.user.email || '',
          gender: response.user.gender || '',
          birth_date: response.user.birth_date || '',
          language: response.user.language || ''
        });
      } catch (error) {
        console.error('Failed to load profile:', error);
        toast({
          title: t('profile.loadError'),
          description: error instanceof Error ? error.message : t('profile.unexpectedError'),
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, [token, t, toast]);

  // Funzione per parsare gli errori JSON:API
  const parseJsonApiErrors = (error: unknown): { fieldErrors: Record<string, string>; generalError: string | null } => {
    const fieldErrors: Record<string, string> = {};
    let generalError: string | null = null;

    try {
      // Prova a parsare come errore JSON:API
      const errorObj = error as { response?: string; message?: string };
      if (errorObj.response) {
        const errorData = JSON.parse(errorObj.response);
        if (errorData.errors && Array.isArray(errorData.errors)) {
          errorData.errors.forEach((err: JsonApiError) => {
            if (err.source?.pointer) {
              const fieldName = err.source.pointer.split('/').pop();
              if (fieldName) {
                fieldErrors[fieldName] = err.detail;
              }
            } else {
              generalError = err.detail;
            }
          });
        }
      } else if (errorObj.message) {
        generalError = errorObj.message;
      }
    } catch {
      generalError = error instanceof Error ? error.message : t('errors.unexpectedError');
    }

    return { fieldErrors, generalError };
  };

  const clearErrors = () => {
    setFieldErrors({});
    setGeneralError(null);
  };

    const handleAvatarUploadSuccess = async (signedId: string) => {
    setAvatarSignedId(signedId);

    // Auto-update profile with the new avatar
    if (!token) return;

    try {
      setUploadingAvatar(true);

      const updateData: ProfileUpdateRequest = {
        user: {
          name: formData.name,
          surname: formData.surname,
          username: formData.username,
          email: formData.email,
          gender: formData.gender,
          birth_date: formData.birth_date || null,
          language: formData.language,
          avatar: signedId
        }
      };

      const response = await profileService.updateProfile(updateData);

      if (response.offline) {
        toast({
          title: t('profile.queuedForSync'),
          description: t('profile.updateQueuedOffline'),
          variant: 'default',
        });
      } else {
        setUser(response.user);
        updateUserData(response.user); // Update auth context for navbar
        setAvatarSignedId(null); // Reset after successful update
        toast({
          title: t('profile.avatarUploadSuccess'),
          description: t('profile.avatarUploadSuccessDescription'),
        });
      }
    } catch (error) {
      console.error('Failed to update profile with new avatar:', error);

      const { fieldErrors, generalError } = parseJsonApiErrors(error);
      setFieldErrors(fieldErrors);
      setGeneralError(generalError);

      // There is no field under the avatar to show its own error at, so the
      // toast carries it (the server refuses a file it cannot scale).
      toast({
        title: t('profile.updateError'),
        description: fieldErrors.avatar || generalError || t('profile.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setUploadingAvatar(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    if (generalError) {
      setGeneralError(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!token) return;

    try {
      setUpdating(true);
      clearErrors(); // Pulisce gli errori precedenti

      const updateData: ProfileUpdateRequest = {
        user: {
          name: formData.name,
          surname: formData.surname,
          username: formData.username,
          email: formData.email,
          gender: formData.gender,
          birth_date: formData.birth_date || null,
          language: formData.language,
          ...(avatarSignedId && { avatar: avatarSignedId })
        }
      };

      const response = await profileService.updateProfile(updateData);

      // Check if this was an offline response (status 202)
      if (response.offline) {
        toast({
          title: t('profile.queuedForSync'),
          description: t('profile.updateQueuedOffline'),
          variant: 'default',
        });
      } else {
        setUser(response.user);
        updateUserData(response.user); // Update auth context for navbar
        setAvatarSignedId(null); // Reset avatar signed ID after successful update
        toast({
          title: t('profile.updateSuccess'),
          description: response.message || t('profile.profileUpdated'),
        });
      }
    } catch (error) {
      console.error('Failed to update profile:', error);

      // Parse json api errors
      const { fieldErrors, generalError } = parseJsonApiErrors(error);
      setFieldErrors(fieldErrors);
      setGeneralError(generalError);

      toast({
        title: t('profile.updateError'),
        description: generalError || t('profile.unexpectedError'),
        variant: 'destructive',
      });
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">{t('profile.loading')}</span>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <User className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h2 className="text-xl font-semibold mb-2">{t('profile.notFound')}</h2>
          <p className="text-muted-foreground">{t('profile.failedToLoad')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Profile Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="h-5 w-5" />
            {t('profile.personalInfo')}
          </CardTitle>
          <CardDescription>
            {t('profile.personalInfoDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {generalError && (
            <div className="bg-destructive/15 border border-destructive/50 rounded-md p-3 flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
              <div className="text-sm text-destructive">
                <p className="font-medium">{t('errors.updateErrorTitle')}</p>
                <p>{generalError}</p>
              </div>
            </div>
          )}

          {/* Avatar Upload Section */}
          <div className="flex flex-col items-center space-y-4 pb-6 border-b">
            <h3 className="text-lg font-medium">{t('profile.profilePicture')}</h3>
            <AvatarUpload
              currentAvatarUrl={user.avatar_url}
              userName={user.full_name}
              onUploadSuccess={handleAvatarUploadSuccess}
              disabled={updating || uploadingAvatar}
              token={token || ''}
            />
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('profile.name')}</Label>
                <Input
                  id="name"
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  disabled={updating}
                  placeholder={t('profile.namePlaceholder')}
                  className={fieldErrors.name ? 'border-destructive' : ''}
                />
                {fieldErrors.name && (
                  <p className="text-sm text-destructive mt-1">{fieldErrors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="surname">{t('profile.surname')}</Label>
                <Input
                  id="surname"
                  type="text"
                  value={formData.surname}
                  onChange={(e) => handleInputChange('surname', e.target.value)}
                  disabled={updating}
                  placeholder={t('profile.surnamePlaceholder')}
                  className={fieldErrors.surname ? 'border-destructive' : ''}
                />
                {fieldErrors.surname && (
                  <p className="text-sm text-destructive mt-1">{fieldErrors.surname}</p>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                {t('profile.email')}
              </Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                disabled={updating}
                placeholder={t('profile.emailPlaceholder')}
                className={fieldErrors.email ? 'border-destructive' : ''}
              />
              {fieldErrors.email && (
                <p className="text-sm text-destructive mt-1">{fieldErrors.email}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="username">{t('profile.username')}</Label>
              <Input
                id="username"
                type="text"
                value={formData.username}
                onChange={(e) => handleInputChange('username', e.target.value)}
                disabled={updating}
                placeholder={t('profile.usernamePlaceholder')}
                className={fieldErrors.username ? 'border-destructive' : ''}
              />
              {fieldErrors.username && (
                <p className="text-sm text-destructive mt-1">{fieldErrors.username}</p>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="gender">{t('profile.gender')}</Label>
                <select
                  id="gender"
                  value={formData.gender}
                  onChange={(e) => handleInputChange('gender', e.target.value)}
                  disabled={updating}
                  className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${fieldErrors.gender ? 'border-destructive' : ''}`}
                >
                  <option value="">{t('profile.selectGender')}</option>
                  <option value="male">{t('profile.male')}</option>
                  <option value="female">{t('profile.female')}</option>
                  <option value="non_binary">{t('profile.nonBinary')}</option>
                  <option value="prefer_not_to_say">{t('profile.preferNotToSay')}</option>
                </select>
                {fieldErrors.gender && (
                  <p className="text-sm text-destructive mt-1">{fieldErrors.gender}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="birth_date">{t('profile.birthDate')}</Label>
                <Input
                  id="birth_date"
                  type="date"
                  max={today}
                  value={formData.birth_date}
                  onChange={(e) => handleInputChange('birth_date', e.target.value)}
                  disabled={updating}
                  className={fieldErrors.birth_date || birthDateProblem ? 'border-destructive' : ''}
                />
                {fieldErrors.birth_date || birthDateProblem ? (
                  <p className="text-sm text-destructive mt-1" data-testid="birth-date-error">
                    {fieldErrors.birth_date || birthDateProblem}
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">{t('profile.birthDateHelp')}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="language">{t('profile.language')}</Label>
                <select
                  id="language"
                  value={formData.language}
                  onChange={(e) => handleInputChange('language', e.target.value)}
                  disabled={updating}
                  className={`flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${fieldErrors.language ? 'border-destructive' : ''}`}
                >
                  <option value="">{t('profile.selectLanguage')}</option>
                  <option value="it">{t('profile.italian')}</option>
                  <option value="en">{t('profile.english')}</option>
                </select>
                {fieldErrors.language && (
                  <p className="text-sm text-destructive mt-1">{fieldErrors.language}</p>
                )}
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                disabled={updating || !!birthDateProblem}
                className="min-w-[120px]"
              >
                {updating ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {t('profile.updating')}
                  </>
                ) : (
                  <>
                    <Settings className="mr-2 h-4 w-4" />
                    {t('profile.updateProfile')}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Membership cards the climber's gyms hold for them. Hides itself when
          there are none. */}
      <MembershipCardsCard />

      {/* The children whose accounts this person holds, and any request still
          waiting on them. A managed account has no profile to visit, so this
          is where a parent sees what exists in their name. Hides itself when
          there is neither. */}
      <MyAthletesCard />

      {/* Push Notifications */}
      <PushNotificationToggle showAsCard={true} />

      {/* Notification Settings */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t('notificationSettings.title')}</p>
              <p className="text-sm text-muted-foreground">{t('notificationSettings.description')}</p>
            </div>
            <Link to="/notification-settings">
              <Button variant="outline" size="sm">
                <Settings className="mr-2 h-4 w-4" />
                {t('notificationSettings.manageSettings')}
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* AI Integration */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">{t('aiIntegration.title')}</p>
              <p className="text-sm text-muted-foreground">{t('aiIntegration.description')}</p>
            </div>
            <Link to="/ai-integration">
              <Button variant="outline" size="sm">
                <Bot className="mr-2 h-4 w-4" />
                {t('aiIntegration.manage')}
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* New Homepage */}
      <FeatureFlagToggle
        title={t('featureFlags.newHomepage.title')}
        description={t('featureFlags.newHomepage.description')}
        enabled={user.feature_flags?.new_homepage ?? false}
        onToggle={async (enabled) => {
          await featureFlagsService.toggleFlag('new_homepage', enabled);
          window.location.reload();
        }}
      />

      {/* Early Adopter */}
      <FeatureFlagToggle
        title={t('featureFlags.earlyAdopter.title')}
        description={t('featureFlags.earlyAdopter.description')}
        enabled={user.feature_flags?.early_adopter ?? false}
        onToggle={async (enabled) => {
          await profileService.updateProfile({
            user: {
              name: formData.name,
              surname: formData.surname,
              username: formData.username,
              email: formData.email,
              gender: formData.gender,
              language: formData.language,
              early_adopter: enabled
            }
          });
          setUser(prev => prev ? { ...prev, feature_flags: { ...prev.feature_flags, early_adopter: enabled } } : prev);
          updateUserData({ ...user, feature_flags: { ...user.feature_flags, early_adopter: enabled } });
          toast({ title: t('featureFlags.earlyAdopter.successTitle'), description: enabled ? t('featureFlags.earlyAdopter.successEnabled') : t('featureFlags.earlyAdopter.successDisabled') });
        }}
      />

      {/* Coach role */}
      <FeatureFlagToggle
        title={t('profile.coach.title')}
        description={t('profile.coach.description')}
        enabled={user.coach ?? false}
        onToggle={async (enabled) => {
          const response = await profileService.updateProfile({
            user: {
              name: formData.name,
              surname: formData.surname,
              username: formData.username,
              email: formData.email,
              gender: formData.gender,
              language: formData.language,
              coach: enabled,
            },
          });
          if (!response.offline) {
            setUser(response.user);
            updateUserData(response.user);
          }
          toast({
            title: t('profile.coach.successTitle'),
            description: enabled ? t('profile.coach.successEnabled') : t('profile.coach.successDisabled'),
          });
        }}
      />

      {/* Coach invite link — only once the coach role is on and a token exists */}
      {user.coach && user.coach_token && (
        <CoachInviteLink
          token={user.coach_token}
          onRegenerated={(updated) => {
            setUser(updated);
            updateUserData(updated);
          }}
        />
      )}

      {/* Account Information */}
      <Card>
        <CardHeader>
          <CardTitle>{t('profile.accountInfo')}</CardTitle>
          <CardDescription>
            {t('profile.accountInfoDescription')}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex justify-between items-center py-2 border-b">
              <span className="font-medium">{t('profile.fullName')}</span>
              <span className="text-muted-foreground">{user.full_name || t('profile.notSet')}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="font-medium">{t('profile.languageName')}</span>
              <span className="text-muted-foreground">{user.language_name || t('profile.notSet')}</span>
            </div>
            <div className="flex justify-between items-center py-2 border-b">
              <span className="font-medium">{t('profile.memberSince')}</span>
              <span className="text-muted-foreground">
                {format(new Date(user.created_at), 'MMM d, yyyy', { locale: dateLocale })}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Language */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Languages className="h-5 w-5" />
            {t('profile.language')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-2">
            <LanguageOption
              active={currentLanguage === 'en'}
              flag="🇬🇧"
              label="English"
              onClick={() => changeLanguage('en')}
            />
            <LanguageOption
              active={currentLanguage === 'it'}
              flag="🇮🇹"
              label="Italiano"
              onClick={() => changeLanguage('it')}
            />
          </div>
        </CardContent>
      </Card>

      {/* Theme */}
      <Card>
        <CardHeader>
          <CardTitle>{t('navbar.theme')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-2">
            <ThemeOption active={theme === 'light'} icon={<Sun className="h-4 w-4" />} label={t('navbar.lightMode')} onClick={() => setTheme('light' as Theme)} />
            <ThemeOption active={theme === 'dark'} icon={<Moon className="h-4 w-4" />} label={t('navbar.darkMode')} onClick={() => setTheme('dark' as Theme)} />
            <ThemeOption active={theme === 'system'} icon={<Monitor className="h-4 w-4" />} label={t('navbar.systemMode')} onClick={() => setTheme('system' as Theme)} />
          </div>
        </CardContent>
      </Card>

      {/* Sign out — must be the very last element */}
      <Card className="border-destructive/30">
        <CardContent className="pt-6">
          <Button
            type="button"
            variant="outline"
            onClick={() => signOut()}
            className="w-full text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
          >
            <LogOut className="mr-2 h-4 w-4" />
            {t('navbar.signOut')}
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

interface LanguageOptionProps {
  active: boolean;
  flag: string;
  label: string;
  onClick: () => void;
}

function LanguageOption({ active, flag, label, onClick }: LanguageOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex items-center justify-center gap-2 rounded-md border py-2 px-3 text-sm font-medium transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-input hover:bg-muted',
      )}
    >
      <span aria-hidden>{flag}</span>
      <span>{label}</span>
    </button>
  );
}

interface ThemeOptionProps {
  active: boolean;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
}

function ThemeOption({ active, icon, label, onClick }: ThemeOptionProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        'flex flex-col items-center justify-center gap-1 rounded-md border py-3 text-xs font-medium transition-colors',
        active
          ? 'border-primary bg-primary text-primary-foreground'
          : 'border-input text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}
